/**
 * Доменные типы приложения. Зеркалят таблицы Supabase.
 * Все суммы хранятся в USDT (числа с плавающей точкой — для UI достаточно).
 */

export interface EquitySnapshot {
  id: number;
  user_id: string;
  equity: number;
  taken_at: string; // ISO
}

export interface AlphaAlert {
  id: number;
  user_id: string;
  prev_equity: number;
  new_equity: number;
  change_pct: number;
  prev_at: string;
  new_at: string;
  is_extreme: boolean; // > 1000%
  created_at: string;
}

export interface BetaAlert {
  id: number;
  user_id: string;
  prev_equity: number;
  new_equity: number;
  change_pct: number;
  prev_hour: string;
  new_hour: string;
  created_at: string;
  /** true — в Telegram было отправлено уведомление (изменение превысило порог). */
  notified: boolean;
}

/** Полный список поддерживаемых базовых активов (underlying).
 *  Используется как фолбэк и единый источник истины во всём приложении.
 *  Первичный источник — Gate API (GET /options/underlyings). */
export const SUPPORTED_SYMBOLS: readonly string[] = [
  'BTCUSDT', 'ETHUSDT', 'LTCUSDT', 'SOLUSDT', 'DOGEUSDT',
  'ADAUSDT', 'XRPUSDT', 'BNBUSDT', 'HYPEUSDT',
  'SUIUSDT', 'XAUTUSDT', 'CLUSDT',
] as const;

/** @deprecated Используйте SUPPORTED_SYMBOLS. Список подгружается динамически из Gate API. */
export const GAMMA_UNDERLYINGS: string[] = [];

export type GammaUnderlying = string;

export interface OptionQuote {
  id: number;
  symbol: string; // напр. BTC-20250131-50000-C
  underlying: string;
  strike: number;
  expiry: string;
  side: 'C' | 'P';
  bid: number;
  ask: number;
  taken_at: string;
}

export interface GammaAlert {
  id: number;
  user_id: string;
  symbol: string;
  prev_ask: number;
  prev_ask_at: string;
  new_bid: number;
  best_bid_size: number | null;
  new_bid_at: string;
  ratio: number; // во сколько раз bid > ask
  link: string;
  created_at: string;
}

export interface DeltaTracking {
  id: number;
  user_id: string;
  gamma_alert_id: number;
  symbol: string;
  bid_at_alert: number;
  underlying_price_at_alert: number | null;
  ask_1m: number | null;
  underlying_price_1m: number | null;
  ask_10m: number | null;
  underlying_price_10m: number | null;
  ask_30m: number | null;
  underlying_price_30m: number | null;
  alert_at: string;
}

export interface EpsilonWatcher {
  id: number;
  user_id: string;
  symbol: string;
  /** 'bid_gte' — bid >= threshold; 'ask_lte' — ask <= threshold */
  kind: 'bid_gte' | 'ask_lte';
  threshold: number;
  is_active: boolean;
  created_at: string;
  triggered_at: string | null;
  /** Фактический bid в момент срабатывания алерта. */
  triggered_bid: number | null;
  /** Фактический ask в момент срабатывания алерта. */
  triggered_ask: number | null;
  /** Включено ли авто-исполнение заявок при срабатывании. */
  auto_execute: boolean;
  /** Количество контрактов в одной заявке для ask-триггера. */
  auto_qty_per_order: number | null;
  /** Максимум заявок в серии для ask-триггера. */
  auto_max_orders: number | null;
  /** Сколько авто-заявок уже было выставлено по этому алерту. */
  auto_orders_executed: number;
  /** TTL заявки в секундах (по умолчанию 300). */
  auto_order_ttl_seconds: number;
  /** Авто-выставление sell после успешного auto-buy (ask). */
  auto_sell_after_buy: boolean;
  /** Цена лимитной sell-заявки после auto-buy. */
  auto_sell_price: number | null;
  /** TTL для auto-sell после buy (если null — заявка GTC). */
  auto_sell_ttl_seconds: number | null;
  /** Режим повтора (только без авто-исполнения): алерт остаётся активным и срабатывает снова после возврата цены через порог. */
  repeat_enabled?: boolean;
  /** Минимальный интервал между повторными срабатываниями, сек (60…86400). */
  repeat_cooldown_seconds?: number;
}

export interface GammaCheckHistory {
  id: number;
  checked_at: string;       // ISO timestamptz
  underlyings_count: number;
  tickers_count: number;
  quotes_count: number;
  alerts_count: number;
  duration_ms: number;
  status: 'ok' | 'error';
  error_message: string | null;
  /** Максимальное соотношение bid / first_ask среди всех проверенных символов за итерацию. */
  max_observed_ratio: number | null;
}

export interface ZetaSnapshot {
  id: number;
  symbol: string;
  bid: number;
  bid_size: number | null;
  ask: number;
  ask_size: number | null;
  bid_size_base?: number | null;
  ask_size_base?: number | null;
  taken_at: string;
  exchange: string;
  is_usdt?: boolean | null;
}

export interface ZetaUnderlyingPrice {
  taken_at: string;
  base: string;
  spot_price: number;
  source: string;
}

export interface ActiveOption {
  symbol: string;     // напр. 'BTC-20250131-50000-C'
  underlying: string; // напр. 'BTCUSDT'
  expiry: string;     // ISO
  strike: number;
  side: 'C' | 'P';
  updated_at: string;
}

export interface ScenarioResult {
  buyExch: string;
  sellExch: string;
  buyAsk: number;
  buyAskSizeBase?: number;
  buyAskSizeEstimated?: boolean;
  sellBid: number;
  sellBidSizeBase?: number;
  sellBidSizeEstimated?: boolean;
  qtyBase: number;
  grossUsd: number;
  feesUsd: number;
  netUsd: number;
  netPctOfPremium: number;
  capitalRequiredUsd?: number;
  capitalLegs?: Array<{
    exchange: string;
    role: 'buy' | 'sell' | 'spot';
    usd: number;
    note: string;
  }>;
  // только для scenario_short
  premiumLockUsd?: number;
  marginLockUsd?: number;
  totalCapitalUsd?: number;
  roiOnCapitalPct?: number;
  aprPct?: number;
  liquidationPriceUsd?: number;
  warnings?: string[];
  perExchangeRequirement?: Record<string, number>;
  minRequiredUsd?: number;
  hedgeRequirement?: {
    exchange: string;
    usd: number;
    leverage: number;
  } | null;
  profitPer100Usd?: number;
  fitsBalance?: boolean;
}

export interface EtaAlert {
  id: number;
  created_at: string;
  normalized_key: string;
  underlying: string;
  expiry: string; // ISO date
  strike: number;
  option_type: 'C' | 'P';
  min_bid: number | null;
  min_bid_size: number | null;
  min_bid_size_base: number | null;
  min_bid_exchange: string | null;
  max_bid: number | null;
  max_bid_size: number | null;
  max_bid_size_base: number | null;
  max_bid_exchange: string | null;
  bid_spread_pct: number | null;
  min_ask: number | null;
  min_ask_size: number | null;
  min_ask_size_base: number | null;
  min_ask_exchange: string | null;
  max_ask: number | null;
  max_ask_size: number | null;
  max_ask_size_base: number | null;
  max_ask_exchange: string | null;
  ask_spread_pct: number | null;
  cross_spread_pct?: number | null;
  arb_no_short: ScenarioResult | null;
  arb_with_short: ScenarioResult | null;
  arb_best_net_usd: number | null;
  arb_best_apr_pct: number | null;
  notified: boolean;
  raw: unknown;
}

export interface EtaProfitableAlert {
  id: number;
  eta_alert_id: number;
  created_at: string;
  net_usd: number;
  apr_pct: number | null;
  required_capital_usd: number | null;
  scenario: 'Без шорта' | 'Со шортом';
  alert: EtaAlert;
}

export interface UserSettings {
  user_id: string;
  gate_api_key: string | null;
  gate_api_secret: string | null;
  notifications_enabled: boolean;
  notify_alpha: boolean;
  notify_beta: boolean;
  notify_gamma: boolean;
  notify_delta: boolean;
  notify_epsilon: boolean;
  notify_zeta: boolean;
  notify_eta: boolean;
  notify_theta: boolean;
  feature_alpha: boolean;
  feature_beta: boolean;
  feature_gamma: boolean;
  feature_delta: boolean;
  feature_epsilon: boolean;
  feature_zeta: boolean;
  feature_eta: boolean;
  feature_theta: boolean;
  eta_threshold_pct: number;
  eta_horizon_days: number;
  eta_min_net_usd: number;
  eta_min_apr_pct: number;
  eta_only_profitable: boolean;
  theta_balance_usd: number;
  theta_autotrade_enabled: boolean;
  theta_kill_switch: boolean;
  theta_leverage_btc: number;
  theta_leverage_eth: number;
  theta_leverage_default: number;
  /** Хедж дельты фьючерсом на WEEX (по умолчанию выключен: лонг+шорт одного опциона уже нейтральны). */
  theta_hedge_enabled: boolean;
  /** Лимиты риска Theta; 0 — без лимита. */
  theta_max_open_positions: number;
  theta_max_position_usd: number;
  theta_max_total_exposure_usd: number;
  theta_min_hours_to_expiry: number;
  theta_max_per_expiry: number;
  /** Отказ от сделки, если живая цена ушла от цены алерта больше чем на N%. */
  theta_max_requote_drift_pct: number;
  /** Порог bid/ask для Gamma-алертов. Алерт срабатывает, когда bid ≥ gamma_ratio × ask. */
  gamma_ratio: number | null;
  /** Минимальный bid (премия опциона, USDT) для Gamma-алерта; 0 — без фильтра. */
  gamma_min_bid?: number | null;
  gamma_exclude_near_expiry: boolean | null;
  gamma_disabled_underlyings: string[] | null;
  updated_at: string;
  /** Последняя ошибка от Gate API (например «INVALID_KEY»). Сбрасывается при успешном опросе. */
  gate_api_last_error: string | null;
  /** Время последнего успешного опроса Gate API. */
  gate_api_last_ok_at: string | null;
  /** Включена ли авто-продажа позиций при EXTREME Alpha-алерте. */
  alpha_auto_sell_enabled: boolean;
  /** Порог изменения баланса для Alpha-алерта (в %). По умолчанию 100. */
  alpha_threshold_pct: number;
  /** Порог изменения баланса для Extreme Alpha-алерта (в %). По умолчанию 1000. */
  alpha_extreme_pct: number;
}

export interface OptionBalance {
  id: number;
  user_id: string;
  exchange: string;
  free_usdt: number;
  raw: unknown;
  taken_at: string;
  error: string | null;
}

export interface ThetaTrade {
  id: number;
  user_id: string;
  eta_alert_id: number | null;
  symbol: string;
  scenario: 'no_short' | 'with_short';
  buy_exchange: string | null;
  sell_exchange: string | null;
  hedge_exchange: string | null;
  qty_base: number | null;
  buy_fill_price: number | null;
  sell_fill_price: number | null;
  hedge_fill_price: number | null;
  hedge_leverage: number | null;
  planned_net_usd: number | null;
  realized_net_usd: number | null;
  status: 'open' | 'closed_profit' | 'closed_loss' | 'closed_expired' | 'failed' | 'partial';
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
  raw_legs: unknown;
}

export interface ThetaPosition {
  id: number;
  user_id: string;
  trade_id: number;
  symbol: string;
  scenario: 'no_short' | 'with_short';
  buy_exchange: string | null;
  sell_exchange: string | null;
  hedge_exchange: string | null;
  qty_base: number | null;
  planned_net_usd: number | null;
  current_net_usd: number | null;
  planned_close_at: string | null;
  expiry_at: string | null;
  status: string;
  opened_at: string;
  updated_at: string;
  strike?: number | null;
  option_type?: 'C' | 'P' | null;
  /** Маржа шорта, заблокированная при открытии. */
  margin_locked_usd?: number | null;
  /** Текущее требование маржи шорта / заблокированная маржа (1.0 = 100%). */
  margin_ratio?: number | null;
  margin_warned_at?: string | null;
  close_reason?: string | null;
  quotes_source?: 'live' | 'snapshot' | null;
}

export interface ThetaDecision {
  id: number;
  user_id: string;
  eta_alert_id: number | null;
  symbol: string | null;
  scenario: string | null;
  net_usd: number | null;
  min_required_usd: number | null;
  reason: string;
  details: string | null;
  created_at: string;
}
